import { debounce } from 'lodash';
import { CounterofferStep } from '../interfaces/interface.offer';
import EventBus from '../services/service.eventBus';
import { updateCounteroffer } from '../services/service.offer';
import { state } from '../store/store.counteroffer';
import { state as offerState } from '../store/store.offer';
import { onChange as onOptimizelyChange, state as optimizelyState } from '../store/store.optimizely';
import { onChange as onPageParamsChange, state as pageParamsState } from '../store/store.page-params';
import track from '../tracking/tracking.counteroffer';
import { parseAmount } from '../utils/utils.counteroffer';
import { asMMDDYYYY } from '../utils/utils.time-format';

const onUpdateFromOffer = (data) => {
  const { hasManualBoost, acceptOrCounterofferAmount, confirmedDate } = data || {};
  state.confirmedDate = confirmedDate && asMMDDYYYY(confirmedDate);
  state.hasManualBoost = hasManualBoost;
  state.amount = acceptOrCounterofferAmount;
  state.hasDataObject = !!data;
  updateShouldDisplay();
};

export const onCounterofferInit = () => {
  onOptimizelyChange('acceptOrCounterofferON', updateShouldDisplay);
  onOptimizelyChange('acceptOrCounterofferUnassignedON', updateShouldDisplay);
  onPageParamsChange('isAdmin', updateShouldDisplay);
  updateShouldDisplay();
};
const updateShouldDisplay = () => {
  const submitted = state.amount >= 0;
  const isAssignedOrUnassignedCounterofferFlagOn =
    (offerState.isAssignedOffer && optimizelyState.acceptOrCounterofferON) || (!offerState.isAssignedOffer && optimizelyState.acceptOrCounterofferUnassignedON);
  const shouldDisplayWhenNotSubmitted = state.hasDataObject && !state.hasManualBoost && isAssignedOrUnassignedCounterofferFlagOn;
  state.shouldDisplay = !pageParamsState.isAdmin && (submitted || shouldDisplayWhenNotSubmitted) && !offerState.isDrOffer;
};

export const onAcceptOffer = () => onUpdateAmount(0);

const onSubmitCounteroffer = () => onUpdateAmount(state.inputAmount);

const onResetCounterofferState = () => {
  state.currentStep = 0;
  state.inputAmount = null;
  state.error = '';
  state.message = '';
  debouncedValidation.cancel();
};

const HIGH_COUNTEROFFER_VARIANCES = [
  { price: 1000, variance: 1.83 },
  { price: 5000, variance: 0.70 },
  { price: 10000, variance: 0.36 },
  { price: 15000, variance: 0.35 },
  { price: 20000, variance: 0.27 },
  { price: 25000, variance: 0.23 },
  { price: 30000, variance: 0.20 },
  { price: 40000, variance: 0.20 },
  { price: 50000, variance: 0.18 },
  { price: 60000, variance: 0.18 },
  { price: 75000, variance: 0.11 },
  { price: 100000, variance: 0.11 },
  { price: Infinity, variance: 0.11 },
];

const getHighCounterofferVariance = (offerValue: number) => {
  return HIGH_COUNTEROFFER_VARIANCES.find(item => offerValue < item.price).variance;
};

const validateCounteroffer = (offerAmount: string) => {
  const offerAmountValue = Number(parseAmount(offerAmount));
  const variance = getHighCounterofferVariance(offerAmountValue);
  const threshold = offerAmountValue * (1 + variance);
  
  if (!state.inputAmount) return '';
  if (state.inputAmount < offerAmountValue) {
    return 'Counteroffer cannot be less than the offer amount.';
  } else if (state.inputAmount >= threshold) {
    return 'This counteroffer is higher than what we usually see.';
  } else {
    return '';
  }
};

// Debounced validation function
const debouncedValidation = debounce((offerAmount: string) => {
  const validationMessage = validateCounteroffer(offerAmount);
  state.message = validationMessage;
}, 2000);

const onUpdateInputAmount = (amount: string, offerAmount?: string) => {
  state.message = ''; // Clear message immediately when user starts typing
  state.inputAmount = parseInt(amount) || null;
  if (state.error && state.inputAmount) {
    state.error = '';
  } 
  debouncedValidation.cancel();

  if (offerAmount && state.inputAmount) {
    debouncedValidation(offerAmount);
  }
  return '';
};

const onInputBlur = (offerAmount?: string) => {
  debouncedValidation.cancel();
  
  if (offerAmount && state.inputAmount) {
    const validationMessage = validateCounteroffer(offerAmount);
    state.message = validationMessage;
  }
};

const onMoveToReview = () => {
  if (state.inputAmount) {
    state.currentStep = CounterofferStep.Review;
    track.counteroffer.make();
  } else {
    state.error = 'Please input your counteroffer.';
  }
};

const onReturnToInput = () => {
  state.currentStep = CounterofferStep.Input;
  track.counteroffer.edit();
};

const onUpdateAmount = (amount: number) => {
  state.isSubmitting = true;
  updateCounteroffer(offerState.offerId, amount)
    .then(() => onUpdateCounterofferSuccess(amount))
    .catch((error) => onUpdateCounterofferFailure(error))
    .finally(() => onUpdateCounterofferComplete());
  EventBus.publish('UpdateCounteroffer', { amount });
};
const onUpdateCounterofferSuccess = (amount: number) => {
  state.amount = amount;
  if (amount > 0) {
    state.currentStep = CounterofferStep.Submitted;
    track.counteroffer.submit();
  } else {
    track.acceptOffer.yes();
  }
  EventBus.publish('UpdateCounterofferSuccess', { amount });
};
const onUpdateCounterofferFailure = (error: object) => {
  EventBus.publish('UpdateCounterofferFailure', error);
};
const onUpdateCounterofferComplete = () => {
  state.isSubmitting = false;
  EventBus.publish('UpdateCounterofferComplete', {});
};

export default {
  onUpdateFromOffer,
  onCounterofferInit,
  onAcceptOffer,
  onSubmitCounteroffer,
  onResetCounterofferState,
  onUpdateInputAmount,
  onInputBlur,
  onMoveToReview,
  onReturnToInput,
  updateShouldDisplay,
};